// src/utils/dexie.ts
import Dexie, { Table } from "dexie";

export interface Result {
  date: string;
  mall_id: string;
  shop_no: string;
  order_count: number;
  payment_composition_amount: number;
  refund_count: number;
  refund_amount: number;
  orders: Array<any>;
  refunds: Array<any>;
  updated_at?: number;
}

// Bump this release-level version whenever settlement/cache behavior changes
// require every local table to be rebuilt. UI-only releases keep it unchanged.
export const SETTLEMENT_DATABASE_CACHE_VERSION = 5;

export interface Order {
  date: string;
  mall_id: string;
  shop_no: string;
  order_id: string;
}

/**
 * Order details cached for refund reference lookups.
 *
 * This store is intentionally keyed by mall/shop/order rather than payment
 * date. A reference order may be outside the selected payment-date range and
 * can be reused by later settlement refreshes.
 */
export interface CachedOrderReference {
  cache_key: string;
  mall_id: string;
  shop_no: string;
  order_id: string;
  fetched_at: number;
  [key: string]: any;
}

export function buildOrderReferenceCacheKey(
  mallId: string,
  shopNo: string | number,
  orderId: string
): string {
  return JSON.stringify([mallId, String(shopNo), orderId.trim()]);
}

export interface Refund {
  date: string;
  mall_id: string;
  shop_no: string;
  order_id: string;
}

const dbname = "st-db-5";

class SettlementDB extends Dexie {
  results!: Table<Result, string>; // string: keyPath 타입(datetime)
  orders!: Table<Order, string>; // string: keyPath 타입(order_id)
  refunds!: Table<Refund, string>; // string: keyPath 타입(order_id)
  orderRefs!: Table<CachedOrderReference, string>;

  constructor() {
    super(dbname);
    this.version(1).stores({
      results:
        "date, mall_id, shop_no, [mall_id+shop_no+date], [mall_id+shop_no]",
      orders:
        "date, mall_id, shop_no, order_id, [mall_id+shop_no+date], [mall_id+shop_no]",
      refunds:
        "date, mall_id, shop_no, order_id, [mall_id+shop_no+date], [mall_id+shop_no]",
    });
    this.version(2).stores({
      orderRefs:
        "cache_key, mall_id, shop_no, order_id, [mall_id+shop_no+order_id]",
    });
  }
}

export const db = new SettlementDB();

export async function addResultToDB(result: Result): Promise<void> {
  await db.results.put(result);
}
